import { Tool } from '@/types/tool';
import AIAssistant from "@/components/AIAssistant";
import BusinessProfileSection from "@/components/BusinessProfileSection";
import MapSection from "@/components/MapSection";
import ToolsSection from "@/components/ToolsSection";
import StoriesFeed from "@/components/StoriesFeed";
import { useFilter } from './FilterContext';
import mapboxgl from 'mapbox-gl';

interface MainContentSectionsProps {
  session: any;
  isEditing: boolean;
  tools: Tool[];
  mapRef: React.MutableRefObject<mapboxgl.Map | null>;
  isMapExpanded: boolean;
  setIsMapExpanded: (expanded: boolean) => void;
  latitude: number | null;
  longitude: number | null;
  onLocationSelect?: (location: { 
    lat: number; 
    lng: number; 
    formattedAddress: string;
    placeId?: string;
    businessDetails?: {
      name?: string;
      phoneNumber?: string;
      website?: string;
      rating?: number;
      userRatingsTotal?: number;
      reviews?: google.maps.places.PlaceReview[];
      photoUrl?: string;
    };
  }) => void;
  categories: string[];
  address: string;
  businessName: string;
  businessPhone: string;
  businessWebsite: string;
  businessDescription: string;
  onCategoriesChange: (value: string[]) => void;
  onEditToggle: () => void;
  onSaveProfile: () => void;
  onBusinessNameChange: (value: string) => void;
  onBusinessPhoneChange: (value: string) => void;
  onBusinessWebsiteChange: (value: string) => void;
  onBusinessDescriptionChange: (value: string) => void;
  isMobile?: boolean;
}

const MainContentSections = ({
  session,
  isEditing,
  tools,
  mapRef,
  isMapExpanded,
  setIsMapExpanded,
  latitude,
  longitude,
  onLocationSelect,
  categories,
  address,
  businessName,
  businessPhone,
  businessWebsite,
  businessDescription,
  onCategoriesChange,
  onEditToggle,
  onSaveProfile,
  onBusinessNameChange,
  onBusinessPhoneChange,
  onBusinessWebsiteChange,
  onBusinessDescriptionChange,
  isMobile,
}: MainContentSectionsProps) => {
  const { searchQuery, selectedCategory, setSearchQuery, setSelectedCategory, filterTools } = useFilter();
  const filteredTools = filterTools(tools);

  const handleBusinessNameClick = (lat: number | undefined, lng: number | undefined) => {
    if (lat && lng && mapRef.current) {
      mapRef.current.flyTo({
        center: [lng, lat],
        zoom: 15,
        duration: 2000,
        essential: true
      });
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-12">
      <div className="lg:col-span-8 space-y-6">
        {session && (
          <div className="animate-fade-in transition-all duration-500 ease-in-out hover:translate-y-[-2px]">
            <AIAssistant />
          </div>
        )}
        
        <div className="animate-fade-in transition-all duration-500 ease-in-out hover:translate-y-[-2px]">
          <MapSection
            isMapExpanded={isMapExpanded}
            onMapExpandToggle={() => setIsMapExpanded(!isMapExpanded)}
            filteredTools={filteredTools}
            isEditing={isEditing && !!session}
            latitude={latitude}
            longitude={longitude}
            onLocationSelect={onLocationSelect}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            mapRef={mapRef}
            isMobile={isMobile}
          />
        </div>

        {session && (
          <div className="animate-fade-in transition-all duration-500 ease-in-out hover:translate-y-[-2px]">
            <BusinessProfileSection
              isEditing={isEditing}
              categories={categories}
              address={address}
              latitude={latitude}
              longitude={longitude}
              businessName={businessName}
              businessPhone={businessPhone}
              businessWebsite={businessWebsite}
              businessDescription={businessDescription}
              onCategoriesChange={onCategoriesChange}
              onEditToggle={onEditToggle}
              onSaveProfile={onSaveProfile}
              onLocationSelect={onLocationSelect}
              onBusinessNameChange={onBusinessNameChange}
              onBusinessPhoneChange={onBusinessPhoneChange}
              onBusinessWebsiteChange={onBusinessWebsiteChange}
              onBusinessDescriptionChange={onBusinessDescriptionChange}
            />
          </div>
        )}
      </div>

      <div className="lg:col-span-4">
        <div className="animate-fade-in sticky top-4 transition-all duration-500">
          <ToolsSection
            searchQuery={searchQuery}
            selectedCategory={selectedCategory}
            onSearchChange={setSearchQuery}
            onCategorySelect={setSelectedCategory}
            featuredTools={filteredTools.filter((tool) => tool.featured)}
            regularTools={filteredTools.filter((tool) => !tool.featured)}
            onBusinessNameClick={handleBusinessNameClick}
          />
        </div>
      </div>

      {session && <StoriesFeed />}
    </div>
  );
};

export default MainContentSections;